import React, { useState, useRef, useEffect } from 'react';
import { db, storage } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useAuthorDashboard } from '../AuthorDashboardContext';
import { doc, setDoc, updateDoc, serverTimestamp, Timestamp, collection as firestoreCollection, query, getDocs } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Headphones, Upload, GripVertical, Plus, X, Search, Music, Image, Loader2, Calendar, Pencil, Save, ShoppingCart, AlertTriangle, CheckCircle
} from 'lucide-react';
import { grantIdForReader, readerMatchesGrant } from '../../lib/freeAccess';

const functions = getFunctions(undefined, 'europe-west2');

const STATUSES = [
  { value: 'draft', label: 'Draft', description: 'Uploaded but not visible to readers' },
  { value: 'scheduled', label: 'Scheduled', description: 'Goes live automatically at the scheduled date and time' },
  { value: 'live', label: 'Live', description: 'Visible and purchaseable immediately' },
  { value: 'hidden', label: 'Hidden', description: 'Not visible to new readers; existing purchasers retain access' },
];

const STATUS_BADGE_CLASSES = {
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
  scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  live: 'bg-green-50 text-green-700 border-green-200',
  hidden: 'bg-yellow-50 text-yellow-700 border-yellow-200',
};

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function estimateDuration(bytes) {
  const minutes = bytes / (1024 * 1024);
  if (minutes < 1) return '< 1 min';
  if (minutes < 60) return Math.round(minutes) + ' min';
  const hrs = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return `${hrs}h ${mins}m`;
}

function formatReleaseDate(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function toDatetimeLocal(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AuthorAudiobooksTab() {
  const { authorId } = useAuth();
  const { audiobooks, readers, basePath, books } = useAuthorDashboard();

  // Form state
  const [selectedBookId, setSelectedBookId] = useState('');
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('0.00');
  const [rentalPrice, setRentalPrice] = useState('0.00');
  const [status, setStatus] = useState('draft');
  const [releaseDate, setReleaseDate] = useState('');

  // Book filter for list view
  const [listBookFilter, setListBookFilter] = useState('');
  const activeBooks = (books || [])
    .filter(b => b.status !== 'archived')
    .sort((a, b) => {
      if (a.id === 'book1') return -1;
      if (b.id === 'book1') return 1;
      return (a.title || '').localeCompare(b.title || '');
    });

  // Default book selections
  useEffect(() => {
    if (activeBooks.length > 0) {
      if (!selectedBookId) setSelectedBookId(activeBooks[0].id);
      if (!listBookFilter) setListBookFilter(activeBooks[0].id);
    }
  }, [activeBooks.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [chapters, setChapters] = useState([]);
  const [grantedReaders, setGrantedReaders] = useState([]);
  const [readerSearch, setReaderSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [submitMessage, setSubmitMessage] = useState('');
  const [preOrderEnabled, setPreOrderEnabled] = useState(false);
  const [preOrderPrice, setPreOrderPrice] = useState('');
  const [preOrderReleaseDate, setPreOrderReleaseDate] = useState('');
  const [visibleToGuests, setVisibleToGuests] = useState(true);
  const [availableToGuests, setAvailableToGuests] = useState(false);
  const fileInputRef = useRef(null);
  const dragItemRef = useRef(null);
  const dragOverRef = useRef(null);

  function handleCoverSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    const reader = new FileReader();
    reader.onload = ev => setCoverPreview(ev.target.result);
    reader.readAsDataURL(file);
  }

  function addChapterFiles(files) {
    const newChapters = Array.from(files)
      .filter(f => f.type === 'audio/mpeg' || f.name.toLowerCase().endsWith('.mp3'))
      .map(f => ({
        id: crypto.randomUUID(),
        file: f,
        title: f.name.replace(/\.mp3$/i, '').replace(/^\d+[\s_.-]*/, ''),
        size: f.size,
      }));
    newChapters.sort((a, b) => a.file.name.localeCompare(b.file.name, undefined, { numeric: true }));
    setChapters(prev => [...prev, ...newChapters]);
  }

  function handleFileDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    addChapterFiles(e.dataTransfer.files);
  }

  function handleFileSelect(e) {
    if (e.target.files) addChapterFiles(e.target.files);
    e.target.value = '';
  }

  function removeChapter(id) {
    setChapters(prev => prev.filter(c => c.id !== id));
  }

  function updateChapterTitle(id, newTitle) {
    setChapters(prev => prev.map(c => c.id === id ? { ...c, title: newTitle } : c));
  }

  function handleDragStart(idx) { dragItemRef.current = idx; }
  function handleDragEnter(idx) { dragOverRef.current = idx; }
  function handleDragEnd() {
    if (dragItemRef.current === null || dragOverRef.current === null) return;
    const reordered = [...chapters];
    const [removed] = reordered.splice(dragItemRef.current, 1);
    reordered.splice(dragOverRef.current, 0, removed);
    setChapters(reordered);
    dragItemRef.current = null;
    dragOverRef.current = null;
  }

  const filteredReaders = readerSearch.trim().length > 1
    ? readers.filter(r => {
        const term = readerSearch.toLowerCase();
        const name = (r.name || '').toLowerCase();
        const email = (r.email || '').toLowerCase();
        return (name.includes(term) || email.includes(term)) && !grantedReaders.some(g => readerMatchesGrant(r, g));
      }).slice(0, 10)
    : [];

  function addReader(reader) {
    setGrantedReaders(prev => [...prev, grantIdForReader(reader)]);
    setReaderSearch('');
  }
  function removeReader(id) {
    setGrantedReaders(prev => prev.filter(r => r !== id));
  }

  function uploadFileWithProgress(fileRef, file, key) {
    return new Promise((resolve, reject) => {
      const task = uploadBytesResumable(fileRef, file);
      task.on('state_changed',
        (snap) => {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          setUploadProgress(prev => ({ ...prev, [key]: pct }));
        },
        reject,
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve(url);
        }
      );
    });
  }

  async function handleSubmit() {
    if (!selectedBookId) { setSubmitMessage('Please select a book.'); return; }
    if (!title.trim()) { setSubmitMessage('Please enter a title.'); return; }
    if (chapters.length === 0) { setSubmitMessage('Please add at least one chapter.'); return; }
    if (status === 'scheduled' && !releaseDate) { setSubmitMessage('Please select a release date for scheduled publication.'); return; }

    setIsSubmitting(true);
    setSubmitMessage('');
    setUploadProgress({});

    try {
      const audiobookDocRef = doc(firestoreCollection(db, basePath, 'audiobooks'));
      const audiobookId = audiobookDocRef.id;
      const storagePath = `authors/${authorId}/audiobooks/${audiobookId}`;

      let coverUrl = '';
      if (coverFile) {
        const coverRef = storageRef(storage, `${storagePath}/cover.jpg`);
        coverUrl = await uploadFileWithProgress(coverRef, coverFile, 'cover');
      }

      const chapterDocs = [];
      for (let i = 0; i < chapters.length; i++) {
        const ch = chapters[i];
        const chapterDocRef = doc(firestoreCollection(db, basePath, 'audiobooks', audiobookId, 'chapters'));
        const chapterFileRef = storageRef(storage, `${storagePath}/chapters/${chapterDocRef.id}.mp3`);
        const audioUrl = await uploadFileWithProgress(chapterFileRef, ch.file, `chapter_${i}`);
        chapterDocs.push({
          ref: chapterDocRef,
          data: {
            title: ch.title.trim() || `Chapter ${i + 1}`,
            order: i,
            audioUrl,
            fileSize: ch.size,
            createdAt: serverTimestamp(),
          },
        });
      }

      const selectedBook = activeBooks.find(b => b.id === selectedBookId);
      const audiobookData = {
        bookId: selectedBookId,
        bookTitle: selectedBook?.title || '',
        title: title.trim(),
        coverUrl,
        price: parseFloat(price) || 0,
        rentalPrice: parseFloat(rentalPrice) || 0,
        chapterCount: chapters.length,
        status,
        releaseDate: status === 'scheduled' ? Timestamp.fromDate(new Date(releaseDate)) : null,
        freeAccessReaders: grantedReaders,
        visibleToGuests,
        availableToGuests,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (preOrderEnabled) {
        audiobookData.preOrder = {
          preOrderEnabled: true,
          preOrderPrice: parseFloat(preOrderPrice) || parseFloat(price) || 0,
          preOrderReleaseDate: preOrderReleaseDate ? Timestamp.fromDate(new Date(preOrderReleaseDate)) : null,
          preOrderCount: 0,
        };
      }

      await setDoc(audiobookDocRef, audiobookData);

      for (const ch of chapterDocs) {
        await setDoc(ch.ref, ch.data);
      }

      // Encrypt the chapters at rest for offline rental playback. This runs
      // alongside the plaintext upload and never replaces it — if it fails the
      // audiobook still uploaded fine and purchase playback is unaffected.
      try {
        setSubmitMessage('Uploaded. Encrypting chapters for rental...');
        const encryptFn = httpsCallable(functions, 'encryptAudiobook');
        await encryptFn({ audiobookId, authorId });
      } catch (encErr) {
        console.warn('Chapter encryption step failed (plaintext upload is intact):', encErr);
      }

      setTitle('');
      setPrice('0.00');
      setStatus('draft');
      setReleaseDate('');
      setCoverFile(null);
      setCoverPreview('');
      setChapters([]);
      setGrantedReaders([]);
      setPreOrderEnabled(false);
      setPreOrderPrice('');
      setPreOrderReleaseDate('');
      setUploadProgress({});
      setSubmitMessage('Audiobook uploaded successfully!');
    } catch (err) {
      setSubmitMessage(`Error: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function updateStatus(audiobook, newStatus, newReleaseDate) {
    try {
      const update = {
        status: newStatus,
        releaseDate: newStatus === 'scheduled' && newReleaseDate
          ? Timestamp.fromDate(new Date(newReleaseDate))
          : null,
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, basePath, 'audiobooks', audiobook.id), update);
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  }

  const totalProgressKeys = Object.keys(uploadProgress);
  const overallProgress = totalProgressKeys.length > 0
    ? Math.round(totalProgressKeys.reduce((sum, k) => sum + (uploadProgress[k] || 0), 0) / totalProgressKeys.length)
    : 0;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">Audiobooks</h1>

      {/* Upload Form */}
      <Card className="border-slate-200">
        <CardContent className="space-y-5 p-6">
          <h2 className="text-lg font-semibold text-slate-800">Upload New Audiobook</h2>

          <div>
            <Label>Book <span className="text-red-500">*</span></Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {activeBooks.map(b => (
                <button
                  key={b.id}
                  onClick={() => setSelectedBookId(b.id)}
                  className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                    selectedBookId === b.id
                      ? 'border-teal-300 bg-teal-50 text-teal-800'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                >
                  {b.title}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Title</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Audiobook title" />
          </div>

          <div>
            <Label>Cover Image</Label>
            <label className="mt-1 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 p-4 hover:border-slate-300">
              {coverPreview ? (
                <img src={coverPreview} alt="Cover" className="h-32 w-24 rounded object-cover" />
              ) : (
                <span className="flex items-center gap-1.5 text-sm text-slate-500">
                  <Image className="h-4 w-4" /> Upload cover image
                </span>
              )}
              <input type="file" accept="image/*" className="hidden" onChange={handleCoverSelect} />
            </label>
          </div>

          <div>
            <Label>Chapters (MP3 files)</Label>
            <div
              className="mt-1 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 p-4 text-center hover:border-slate-300"
              onDrop={handleFileDrop}
              onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
            >
              <Music className="mx-auto mb-1 h-6 w-6 text-slate-400" />
              <p className="text-sm text-slate-500">Drag and drop MP3 files here, or click to browse</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".mp3,audio/mpeg"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>

            {chapters.length > 0 && (
              <div className="mt-3 space-y-2">
                {chapters.map((ch, idx) => (
                  <div
                    key={ch.id}
                    className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2"
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragEnter={() => handleDragEnter(idx)}
                    onDragEnd={handleDragEnd}
                    onDragOver={e => e.preventDefault()}
                  >
                    <GripVertical className="h-4 w-4 flex-shrink-0 cursor-grab text-slate-300" />
                    <span className="w-6 text-center text-xs font-medium text-slate-400">{idx + 1}</span>
                    <Input
                      value={ch.title}
                      onChange={e => updateChapterTitle(ch.id, e.target.value)}
                      className="flex-1"
                      placeholder={`Chapter ${idx + 1}`}
                    />
                    <span className="text-xs text-slate-400 whitespace-nowrap">{formatFileSize(ch.size)}</span>
                    <span className="text-xs text-slate-400 whitespace-nowrap">{estimateDuration(ch.size)}</span>
                    {uploadProgress[`chapter_${idx}`] !== undefined && (
                      <span className="text-xs font-medium text-teal-600">{uploadProgress[`chapter_${idx}`]}%</span>
                    )}
                    <button onClick={() => removeChapter(ch.id)} className="text-slate-400 hover:text-red-500">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label>Price (GBP)</Label>
            <div className="relative mt-1 max-w-[200px]">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">£</span>
              <Input
                className="pl-7"
                value={price}
                onChange={e => setPrice(e.target.value)}
                placeholder="0.00"
                type="number"
                min="0"
                step="0.01"
              />
            </div>
            <p className="mt-1 text-xs text-slate-400">Set to £0.00 to make it free for all readers.</p>
          </div>

          <div>
            <Label>Rental price (GBP)</Label>
            <div className="relative mt-1 max-w-[200px]">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">£</span>
              <Input
                className="pl-7"
                value={rentalPrice}
                onChange={e => setRentalPrice(e.target.value)}
                placeholder="0.00"
                type="number"
                min="0"
                step="0.01"
              />
            </div>
            <p className="mt-1 text-xs text-slate-400">A six-month rental, sold alongside the full purchase. Set to £0.00 to not offer rentals.</p>
          </div>

          {/* Publication status */}
          <div>
            <Label>Publication Status</Label>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {STATUSES.map(s => (
                <button
                  key={s.value}
                  onClick={() => setStatus(s.value)}
                  className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                    status === s.value
                      ? 'border-teal-300 bg-teal-50 text-teal-800'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <span className="font-medium">{s.label}</span>
                  <p className="mt-0.5 text-[11px] leading-tight text-slate-400">{s.description}</p>
                </button>
              ))}
            </div>
            {status === 'scheduled' && (
              <div className="mt-3 max-w-xs">
                <Label className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" /> Release Date and Time
                </Label>
                <Input
                  type="datetime-local"
                  value={releaseDate}
                  onChange={e => setReleaseDate(e.target.value)}
                  className="mt-1"
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>
            )}
          </div>

          {/* Pre-order settings */}
          <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5">
                <ShoppingCart className="h-4 w-4 text-teal-600" /> Pre-Order Settings
              </Label>
              <button
                onClick={() => setPreOrderEnabled(!preOrderEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  preOrderEnabled ? 'bg-teal-600' : 'bg-slate-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                  preOrderEnabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            {preOrderEnabled && (
              <>
                <div className="max-w-[200px]">
                  <Label className="text-xs">Pre-Order Price (GBP)</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">£</span>
                    <Input
                      className="pl-7"
                      value={preOrderPrice}
                      onChange={e => setPreOrderPrice(e.target.value)}
                      placeholder={price || '0.00'}
                      type="number"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <p className="mt-1 text-[11px] text-slate-400">Can be lower than launch price as an early-bird incentive.</p>
                </div>
                <div className="max-w-xs">
                  <Label className="text-xs flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" /> Expected Release Date
                  </Label>
                  <Input
                    type="date"
                    value={preOrderReleaseDate}
                    onChange={e => setPreOrderReleaseDate(e.target.value)}
                    className="mt-1"
                  />
                  <p className="mt-1 text-[11px] text-slate-400">Shown to readers. Does not auto-fulfil — you control when to charge.</p>
                </div>
              </>
            )}
          </div>

          {/* Guest visibility controls */}
          <div className="space-y-2 rounded-lg border border-slate-200 p-3">
            <p className="text-sm font-medium text-slate-700">Guest Store Visibility</p>
            <div className="flex items-center gap-3">
              <input type="checkbox" checked={visibleToGuests} onChange={e => {
                setVisibleToGuests(e.target.checked);
                if (!e.target.checked) setAvailableToGuests(false);
              }} className="h-4 w-4 rounded border-slate-300 text-teal-600" />
              <div>
                <Label className="text-sm">Visible to guests</Label>
                <p className="text-xs text-slate-400">Show on the guest storefront (greyed out if not available)</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" checked={availableToGuests} onChange={e => {
                setAvailableToGuests(e.target.checked);
                if (e.target.checked) setVisibleToGuests(true);
              }} className="h-4 w-4 rounded border-slate-300 text-teal-600" disabled={!visibleToGuests} />
              <div>
                <Label className={`text-sm ${!visibleToGuests ? 'text-slate-400' : ''}`}>Available to guests</Label>
                <p className="text-xs text-slate-400">Guests can purchase this audiobook from the store</p>
              </div>
            </div>
          </div>

          {/* Grant free access */}
          <div>
            <Label>Grant Free Access</Label>
            <p className="mb-2 text-xs text-slate-400">Search readers by name or email to grant free access.</p>
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                className="pl-9"
                value={readerSearch}
                onChange={e => setReaderSearch(e.target.value)}
                placeholder="Search readers..."
              />
            </div>
            {filteredReaders.length > 0 && (
              <div className="mt-1 max-w-md rounded-lg border border-slate-200 bg-white shadow-sm">
                {filteredReaders.map(r => (
                  <button
                    key={r.id}
                    onClick={() => addReader(r)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                  >
                    <Plus className="h-3 w-3 text-teal-600" />
                    <span className="font-medium text-slate-700">{r.name || 'Unnamed'}</span>
                    <span className="text-slate-400">{r.email || ''}</span>
                  </button>
                ))}
              </div>
            )}
            {grantedReaders.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {grantedReaders.map(rid => {
                  const r = readers.find(x => readerMatchesGrant(x, rid));
                  return (
                    <Badge key={rid} className="gap-1 bg-teal-50 text-teal-700 border-teal-200">
                      {r?.name || r?.email || rid}
                      <button onClick={() => removeReader(rid)} className="hover:text-red-500">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>

          {isSubmitting && totalProgressKeys.length > 0 && (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm text-slate-600">
                <span>Uploading files...</span>
                <span>{overallProgress}%</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-teal-600 transition-all" style={{ width: `${overallProgress}%` }} />
              </div>
            </div>
          )}

          {submitMessage && (
            <div className={`rounded-lg p-3 text-sm ${
              submitMessage.startsWith('Error') ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-green-50 text-green-600 border border-green-200'
            }`}>
              {submitMessage}
            </div>
          )}

          <Button
            className="gap-1.5 bg-teal-600 hover:bg-teal-700 text-white"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</> : <><Upload className="h-4 w-4" /> Upload Audiobook</>}
          </Button>
        </CardContent>
      </Card>

      {/* Existing audiobooks table */}
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="mb-4 space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">Your Audiobooks</h2>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setListBookFilter('all')}
                className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                  listBookFilter === 'all'
                    ? 'border-teal-300 bg-teal-50 text-teal-800'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                All Books
              </button>
              {activeBooks.map(b => (
                <button
                  key={b.id}
                  onClick={() => setListBookFilter(b.id)}
                  className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                    listBookFilter === b.id
                      ? 'border-teal-300 bg-teal-50 text-teal-800'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                >
                  {b.title}
                </button>
              ))}
            </div>
          </div>
          {audiobooks.filter(ab => listBookFilter === 'all' || ab.bookId === listBookFilter).length === 0 ? (
            <p className="text-sm text-slate-500">No audiobooks uploaded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left">
                    <th className="pb-2 font-medium text-slate-500">Title</th>
                    <th className="pb-2 font-medium text-slate-500">Price</th>
                    <th className="pb-2 font-medium text-slate-500">Chapters</th>
                    <th className="pb-2 font-medium text-slate-500">Status</th>
                    <th className="pb-2 font-medium text-slate-500">Change Status</th>
                    <th className="pb-2 font-medium text-slate-500"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {audiobooks.filter(ab => listBookFilter === 'all' || ab.bookId === listBookFilter).map(ab => (
                    <AudiobookRow
                      key={ab.id}
                      audiobook={ab}
                      onUpdateStatus={updateStatus}
                      basePath={basePath}
                      readers={readers}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AudiobookRow({ audiobook, onUpdateStatus, basePath, readers }) {
  const ab = audiobook;
  const currentStatus = ab.status || (ab.published ? 'live' : 'draft');
  const [pendingDate, setPendingDate] = useState(toDatetimeLocal(ab.releaseDate));
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(ab.title || '');
  const [editPrice, setEditPrice] = useState(String(ab.price || '0.00'));
  const [editRentalPrice, setEditRentalPrice] = useState(String(ab.rentalPrice || '0.00'));
  const [editStatus, setEditStatus] = useState(currentStatus);
  const [editReleaseDate, setEditReleaseDate] = useState(toDatetimeLocal(ab.releaseDate));
  const [editFreeAccess, setEditFreeAccess] = useState(ab.freeAccessReaders || []);
  const [editReaderSearch, setEditReaderSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [editMessage, setEditMessage] = useState('');
  const [editPreOrderEnabled, setEditPreOrderEnabled] = useState(ab.preOrder?.preOrderEnabled || false);
  const [editPreOrderPrice, setEditPreOrderPrice] = useState(String(ab.preOrder?.preOrderPrice || ''));
  const [editPreOrderReleaseDate, setEditPreOrderReleaseDate] = useState(
    ab.preOrder?.preOrderReleaseDate
      ? (ab.preOrder.preOrderReleaseDate.toDate ? ab.preOrder.preOrderReleaseDate.toDate().toISOString().slice(0, 10) : new Date(ab.preOrder.preOrderReleaseDate).toISOString().slice(0, 10))
      : ''
  );
  const [showPreOrders, setShowPreOrders] = useState(false);
  const [preOrders, setPreOrders] = useState([]);
  const [loadingPreOrders, setLoadingPreOrders] = useState(false);
  const [fulfillingPreOrders, setFulfillingPreOrders] = useState(false);
  const [showFulfilConfirm, setShowFulfilConfirm] = useState(false);
  const [fulfilResult, setFulfilResult] = useState(null);

  function handleStatusChange(newStatus) {
    if (newStatus === 'scheduled') {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
      onUpdateStatus(ab, newStatus, null);
    }
  }

  function confirmScheduled() {
    if (!pendingDate) return;
    onUpdateStatus(ab, 'scheduled', pendingDate);
    setShowDatePicker(false);
  }

  function openEdit() {
    setEditTitle(ab.title || '');
    setEditPrice(String(ab.price || '0.00'));
    setEditRentalPrice(String(ab.rentalPrice || '0.00'));
    setEditStatus(currentStatus);
    setEditReleaseDate(toDatetimeLocal(ab.releaseDate));
    setEditFreeAccess(ab.freeAccessReaders || []);
    setEditPreOrderEnabled(ab.preOrder?.preOrderEnabled || false);
    setEditPreOrderPrice(String(ab.preOrder?.preOrderPrice || ''));
    setEditPreOrderReleaseDate(
      ab.preOrder?.preOrderReleaseDate
        ? (ab.preOrder.preOrderReleaseDate.toDate ? ab.preOrder.preOrderReleaseDate.toDate().toISOString().slice(0, 10) : new Date(ab.preOrder.preOrderReleaseDate).toISOString().slice(0, 10))
        : ''
    );
    setEditMessage('');
    setIsEditing(true);
  }

  async function loadPreOrders() {
    setLoadingPreOrders(true);
    try {
      const snap = await getDocs(
        query(firestoreCollection(db, basePath, 'audiobooks', ab.id, 'preOrders'))
      );
      const items = [];
      snap.forEach(d => items.push({ id: d.id, ...d.data() }));
      items.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(0);
        return bTime - aTime;
      });
      setPreOrders(items);
    } catch (err) {
      console.error('Failed to load pre-orders:', err);
    }
    setLoadingPreOrders(false);
  }

  async function handleFulfilPreOrders() {
    setFulfillingPreOrders(true);
    setFulfilResult(null);
    try {
      const fulfilFn = httpsCallable(functions, 'fulfilAudiobookPreOrders');
      const result = await fulfilFn({ audiobookId: ab.id });
      setFulfilResult(result.data);
      setShowFulfilConfirm(false);
      await loadPreOrders();
    } catch (err) {
      setFulfilResult({ error: err.message });
    }
    setFulfillingPreOrders(false);
  }

  const editFilteredReaders = editReaderSearch.trim().length > 1
    ? (readers || []).filter(r => {
        const term = editReaderSearch.toLowerCase();
        const name = (r.name || '').toLowerCase();
        const email = (r.email || '').toLowerCase();
        return (name.includes(term) || email.includes(term)) && !editFreeAccess.some(g => readerMatchesGrant(r, g));
      }).slice(0, 10)
    : [];

  async function saveEdit() {
    if (!editTitle.trim()) { setEditMessage('Title is required.'); return; }
    if (editStatus === 'scheduled' && !editReleaseDate) { setEditMessage('Release date required for scheduled status.'); return; }
    setIsSaving(true);
    setEditMessage('');
    try {
      const update = {
        title: editTitle.trim(),
        price: parseFloat(editPrice) || 0,
        rentalPrice: parseFloat(editRentalPrice) || 0,
        status: editStatus,
        releaseDate: editStatus === 'scheduled' && editReleaseDate
          ? Timestamp.fromDate(new Date(editReleaseDate))
          : null,
        freeAccessReaders: editFreeAccess,
        updatedAt: serverTimestamp(),
        preOrder: editPreOrderEnabled ? {
          preOrderEnabled: true,
          preOrderPrice: parseFloat(editPreOrderPrice) || parseFloat(editPrice) || 0,
          preOrderReleaseDate: editPreOrderReleaseDate ? Timestamp.fromDate(new Date(editPreOrderReleaseDate)) : null,
          preOrderCount: ab.preOrder?.preOrderCount || 0,
        } : {
          preOrderEnabled: false,
          preOrderPrice: 0,
          preOrderReleaseDate: null,
          preOrderCount: ab.preOrder?.preOrderCount || 0,
        },
      };
      await updateDoc(doc(db, basePath, 'audiobooks', ab.id), update);
      setIsEditing(false);
    } catch (err) {
      setEditMessage(`Error: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  }

  if (isEditing) {
    return (
      <tr>
        <td colSpan={6} className="py-4">
          <div className="space-y-4 rounded-lg border border-teal-200 bg-teal-50/30 p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">Edit Audiobook</h3>
              <button onClick={() => setIsEditing(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Title</Label>
                <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} />
              </div>
              <div>
                <Label>Price (GBP)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">£</span>
                  <Input className="pl-7" value={editPrice} onChange={e => setEditPrice(e.target.value)} type="number" min="0" step="0.01" />
                </div>
              </div>
              <div>
                <Label>Rental price (GBP)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">£</span>
                  <Input className="pl-7" value={editRentalPrice} onChange={e => setEditRentalPrice(e.target.value)} type="number" min="0" step="0.01" />
                </div>
                <p className="mt-1 text-[11px] text-slate-400">Six-month rental, sold alongside the full purchase. £0.00 = no rental.</p>
              </div>
            </div>

            <div>
              <Label>Status</Label>
              <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {STATUSES.map(s => (
                  <button
                    key={s.value}
                    onClick={() => setEditStatus(s.value)}
                    className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                      editStatus === s.value
                        ? 'border-teal-300 bg-teal-50 text-teal-800'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <span className="font-medium">{s.label}</span>
                  </button>
                ))}
              </div>
              {editStatus === 'scheduled' && (
                <div className="mt-2 max-w-xs">
                  <Label className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Release Date</Label>
                  <Input type="datetime-local" value={editReleaseDate} onChange={e => setEditReleaseDate(e.target.value)} className="mt-1" min={new Date().toISOString().slice(0, 16)} />
                </div>
              )}
            </div>

            <div>
              <Label>Free Access Readers</Label>
              <div className="relative mt-1 max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input className="pl-9" value={editReaderSearch} onChange={e => setEditReaderSearch(e.target.value)} placeholder="Search readers to grant access..." />
              </div>
              {editFilteredReaders.length > 0 && (
                <div className="mt-1 max-w-md rounded-lg border border-slate-200 bg-white shadow-sm">
                  {editFilteredReaders.map(r => (
                    <button key={r.id} onClick={() => { setEditFreeAccess(prev => [...prev, grantIdForReader(r)]); setEditReaderSearch(''); }} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50">
                      <Plus className="h-3 w-3 text-teal-600" />
                      <span className="font-medium text-slate-700">{r.name || 'Unnamed'}</span>
                      <span className="text-slate-400">{r.email || ''}</span>
                    </button>
                  ))}
                </div>
              )}
              {editFreeAccess.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {editFreeAccess.map(rid => {
                    const r = (readers || []).find(x => readerMatchesGrant(x, rid));
                    return (
                      <Badge key={rid} className="gap-1 bg-teal-50 text-teal-700 border-teal-200">
                        {r?.name || r?.email || rid}
                        <button onClick={() => setEditFreeAccess(prev => prev.filter(id => id !== rid))} className="hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pre-order settings */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5">
                  <ShoppingCart className="h-4 w-4 text-teal-600" /> Pre-Order Settings
                </Label>
                <button
                  onClick={() => setEditPreOrderEnabled(!editPreOrderEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    editPreOrderEnabled ? 'bg-teal-600' : 'bg-slate-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                    editPreOrderEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              {editPreOrderEnabled && (
                <>
                  <div className="max-w-[200px]">
                    <Label className="text-xs">Pre-Order Price (GBP)</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">£</span>
                      <Input className="pl-7" value={editPreOrderPrice} onChange={e => setEditPreOrderPrice(e.target.value)} placeholder={editPrice || '0.00'} type="number" min="0" step="0.01" />
                    </div>
                    <p className="mt-1 text-[11px] text-slate-400">Can be lower than launch price as an early-bird incentive.</p>
                  </div>
                  <div className="max-w-xs">
                    <Label className="text-xs flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Expected Release Date</Label>
                    <Input type="date" value={editPreOrderReleaseDate} onChange={e => setEditPreOrderReleaseDate(e.target.value)} className="mt-1" />
                    <p className="mt-1 text-[11px] text-slate-400">Shown to readers. Does not auto-fulfil.</p>
                  </div>
                </>
              )}
            </div>

            {editMessage && (
              <div className={`rounded-lg p-3 text-sm ${editMessage.startsWith('Error') ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-green-50 text-green-600 border border-green-200'}`}>
                {editMessage}
              </div>
            )}

            <div className="flex gap-2">
              <Button className="gap-1.5 bg-teal-600 hover:bg-teal-700 text-white" onClick={saveEdit} disabled={isSaving}>
                {isSaving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</> : <><Save className="h-4 w-4" /> Save Changes</>}
              </Button>
              <Button variant="outline" className="border-slate-200" onClick={() => setIsEditing(false)}>Cancel</Button>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  // Pre-orders management view
  if (showPreOrders) {
    const authorisedOrders = preOrders.filter(o => o.status === 'authorised');
    const totalAuthorised = authorisedOrders.reduce((sum, o) => sum + (o.amount || 0), 0);

    return (
      <tr>
        <td colSpan={6} className="py-4">
          <div className="space-y-4 rounded-lg border border-blue-200 bg-blue-50/30 p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">
                Pre-Orders for {ab.title}
                {ab.preOrder?.preOrderCount > 0 && (
                  <Badge className="ml-2 bg-blue-50 text-blue-700 border-blue-200">{ab.preOrder.preOrderCount} total</Badge>
                )}
              </h3>
              <button onClick={() => setShowPreOrders(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            {loadingPreOrders ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading pre-orders...
              </div>
            ) : preOrders.length === 0 ? (
              <p className="text-sm text-slate-500">No pre-orders yet.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 text-left">
                        <th className="pb-2 font-medium text-slate-500">Reader</th>
                        <th className="pb-2 font-medium text-slate-500">Email</th>
                        <th className="pb-2 font-medium text-slate-500">Amount</th>
                        <th className="pb-2 font-medium text-slate-500">Date</th>
                        <th className="pb-2 font-medium text-slate-500">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {preOrders.map(po => (
                        <tr key={po.id}>
                          <td className="py-2 text-slate-700">{po.readerName || 'Unknown'}</td>
                          <td className="py-2 text-slate-500">{po.readerEmail}</td>
                          <td className="py-2 text-slate-700">£{Number(po.amount).toFixed(2)}</td>
                          <td className="py-2 text-slate-500">{po.createdAt?.toDate?.()?.toLocaleDateString('en-GB') || ''}</td>
                          <td className="py-2">
                            <Badge className={
                              po.status === 'authorised' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                              po.status === 'captured' ? 'bg-green-50 text-green-700 border-green-200' :
                              po.status === 'cancelled' ? 'bg-red-50 text-red-700 border-red-200' :
                              'bg-slate-100 text-slate-600 border-slate-200'
                            }>
                              {po.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {authorisedOrders.length > 0 && (
                  <div className="space-y-2">
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm">
                      <span className="font-medium text-blue-800">{authorisedOrders.length} pending</span>
                      <span className="text-blue-600"> — £{totalAuthorised.toFixed(2)} total authorised</span>
                    </div>

                    {!showFulfilConfirm ? (
                      <Button
                        className="gap-1.5 bg-teal-600 hover:bg-teal-700 text-white"
                        onClick={() => setShowFulfilConfirm(true)}
                        disabled={fulfillingPreOrders}
                      >
                        <CheckCircle className="h-4 w-4" /> Fulfil All Pre-Orders
                      </Button>
                    ) : (
                      <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4 space-y-3">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-yellow-800">
                              This will charge {authorisedOrders.length} reader{authorisedOrders.length !== 1 ? 's' : ''} £{totalAuthorised.toFixed(2)} total and grant them immediate access.
                            </p>
                            <p className="text-xs text-yellow-600 mt-1">This cannot be undone.</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            className="gap-1.5 bg-teal-600 hover:bg-teal-700 text-white"
                            onClick={handleFulfilPreOrders}
                            disabled={fulfillingPreOrders}
                          >
                            {fulfillingPreOrders ? <><Loader2 className="h-4 w-4 animate-spin" /> Fulfilling...</> : 'Yes, fulfil all'}
                          </Button>
                          <Button variant="outline" className="border-slate-200" onClick={() => setShowFulfilConfirm(false)}>Cancel</Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {fulfilResult && !fulfilResult.error && (
                  <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
                    <CheckCircle className="inline h-4 w-4 mr-1" />
                    {fulfilResult.captured} captured successfully.
                    {fulfilResult.failed > 0 && <span className="text-red-600 ml-2">{fulfilResult.failed} failed.</span>}
                  </div>
                )}
                {fulfilResult?.error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">Error: {fulfilResult.error}</div>
                )}
              </>
            )}
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td className="py-3">
        <div className="flex items-center gap-2">
          {ab.coverUrl ? (
            <img src={ab.coverUrl} alt="" className="h-8 w-6 rounded object-cover" />
          ) : (
            <Headphones className="h-5 w-5 text-slate-300" />
          )}
          <span className="font-medium text-slate-700">{ab.title}</span>
        </div>
      </td>
      <td className="py-3 text-slate-600">
        {ab.price > 0 ? `£${Number(ab.price).toFixed(2)}` : 'Free'}
      </td>
      <td className="py-3 text-slate-600">{ab.chapterCount || 0}</td>
      <td className="py-3">
        <div className="flex items-center gap-1.5">
          <Badge className={STATUS_BADGE_CLASSES[currentStatus] || STATUS_BADGE_CLASSES.draft}>
            {currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)}
          </Badge>
          {ab.preOrder?.preOrderEnabled && (
            <Badge className="bg-blue-50 text-blue-700 border-blue-200">Pre-Order</Badge>
          )}
          {currentStatus === 'scheduled' && ab.releaseDate && (
            <span className="text-xs text-blue-600">{formatReleaseDate(ab.releaseDate)}</span>
          )}
        </div>
      </td>
      <td className="py-3">
        <div className="flex items-center gap-2">
          <select
            value={showDatePicker ? 'scheduled' : currentStatus}
            onChange={e => handleStatusChange(e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 focus:border-teal-300 focus:outline-none focus:ring-1 focus:ring-teal-300"
          >
            {STATUSES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          {showDatePicker && (
            <div className="flex items-center gap-1.5">
              <Input
                type="datetime-local"
                value={pendingDate}
                onChange={e => setPendingDate(e.target.value)}
                className="h-8 w-auto text-xs"
                min={new Date().toISOString().slice(0, 16)}
              />
              <Button size="sm" className="h-8 bg-teal-600 hover:bg-teal-700 text-white text-xs px-2" onClick={confirmScheduled}>
                Set
              </Button>
            </div>
          )}
        </div>
      </td>
      <td className="py-3">
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="gap-1 border-slate-200 text-slate-600 hover:border-teal-300 hover:text-teal-700" onClick={openEdit}>
            <Pencil className="h-3.5 w-3.5" /> Edit
          </Button>
          {ab.preOrder?.preOrderEnabled && (
            <Button size="sm" variant="outline" className="gap-1 border-blue-200 text-blue-600 hover:border-blue-300 hover:bg-blue-50" onClick={() => { setShowPreOrders(true); loadPreOrders(); }}>
              <ShoppingCart className="h-3.5 w-3.5" /> Pre-Orders{ab.preOrder?.preOrderCount > 0 ? ` (${ab.preOrder.preOrderCount})` : ''}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}
